/* eslint-disable no-console */
import { invoke } from '@tauri-apps/api/core'
import { nanoid } from 'nanoid'

import { Result } from '@blade/std-lib'
import { isBrowserLocalMode, PlatformUtil } from '@root/shared/core'
import { platformInfo } from '@root/shared/core/platform-info'
import { LOG_API_ENABLED } from '@root/shared/environment'
import { recordApiCall } from '@root/shared/lib/browser-timing'

const PROXY_PORT = 12_582

const methodStyles: Record<string, string> = {
  GET: 'background: #61affe1a; color: #61affe;',
  POST: 'background: #49cc901a; color: #49cc90;',
  PUT: 'background: #fca1301a; color: #fca130;',
  DELETE: 'background: #f93e3e1a; color: #f93e3e;',
  PATCH: 'background: #50e3c21a; color: #50e3c2;',
  OPTIONS: 'background: #0d5aa71a; color: #0d5aa7;',
  HEAD: 'background: #9012fe1a; color: #9012fe;',
  // default
  DEFAULT: '',
}

type Args = {
  withDeviceId?: boolean
  withPlatform?: boolean
  proxySubPath?: string
}

const readLocalToken = (): string | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fromWindow = (window as any).__DELUGE_LOCAL_TOKEN__ as
    | string
    | undefined
  if (fromWindow) {
    return fromWindow
  }
  try {
    return sessionStorage.getItem('__DELUGE_LOCAL_TOKEN__') ?? undefined
  } catch {
    return undefined
  }
}

const isTauriMobile = (): boolean =>
  PlatformUtil.is('tauri') &&
  (platformInfo.os === 'ios' || platformInfo.os === 'android')

const applyProxy = (
  params: RequestInfo | URL,
  fetchInit: RequestInit,
  headers: Headers,
  proxySubPath: string,
): RequestInfo | URL => {
  // A browser talks to the API itself.
  //
  // The proxy below exists for the desktop app, whose webview has no origin
  // a cookie can belong to: it borrows the local process's. A browser has
  // one, and when the application and the API are served from the same
  // `.localhost` name their cookies are same-site — so the request goes
  // where it was addressed, carrying the session, and the terminal works
  // without a desktop process running beside it.
  if (isBrowserLocalMode()) {
    fetchInit.credentials = 'include'
    return params
  }

  if (!PlatformUtil.is('tauri')) {
    return params
  }

  const originalUrl =
    params instanceof URL ? params : new URL(params.toString())
  const proxyUrl = `http://localhost:${PROXY_PORT}/${proxySubPath}${originalUrl.pathname}${originalUrl.search}`

  const localToken = readLocalToken()
  if (localToken) {
    headers.set('x-deluge-local-token', localToken)
  }

  return proxyUrl
}

/**
 * The path as it was actually called.
 *
 * Distinct from `extractPathname` below, which reverses the segments so the
 * telling part of a path comes first in a console line. Read as a metric
 * label that reversal turns `/api/support/ws-ticket` into
 * `/ws-ticket/support`, which is unreadable and matches no route anybody
 * would look for.
 */
const pathOf = (params: RequestInfo | URL): string => {
  try {
    return (params instanceof URL ? params : new URL(params.toString()))
      .pathname
  } catch {
    return ''
  }
}

const extractPathname = (params: RequestInfo | URL): string =>
  Result.try({
    try: () => {
      const [_, _api, ...rest] = (
        params instanceof URL ? params : new URL(params.toString())
      ).pathname.split('/')
      return rest.reverse().join('/')
    },
    catch: () => '',
  }).pipe(Result.merge)

interface MobileApiResponse {
  status: number
  headers: Record<string, string>
  body: Array<number>
}

const NULL_BODY_STATUSES = new Set([204, 205, 304])

const resolveRequestUrl = (input: RequestInfo | URL): string => {
  if (input instanceof URL) {
    return input.toString()
  }
  if (typeof input === 'string') {
    return input
  }
  return input.url
}

const mobileApiFetch = async (
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> => {
  const url = resolveRequestUrl(input)

  const headers: Record<string, string> = {}
  for (const [key, value] of new Headers(init.headers).entries()) {
    headers[key] = value
  }

  const body =
    init.body == undefined
      ? undefined
      : [
          ...new Uint8Array(
            await new Response(init.body as BodyInit).arrayBuffer(),
          ),
        ]

  const res = await invoke<MobileApiResponse>('mobile_api_fetch', {
    method: init.method ?? 'GET',
    url,
    headers,
    body,
  })

  const responseBody = NULL_BODY_STATUSES.has(res.status)
    ? undefined
    : new Uint8Array(res.body)

  return new Response(responseBody, {
    status: res.status,
    headers: res.headers,
  })
}

export const createCustomFetch =
  (args?: Args): typeof globalThis.fetch =>
  async (params, init = {}) => {
    const {
      withDeviceId = true,
      withPlatform = true,
      proxySubPath = 'proxy',
    } = args ?? {}

    const headers = new Headers(init.headers)
    headers.set('x-request-id', nanoid())
    headers.set('User-Agent', navigator.userAgent)

    if (withDeviceId) {
      const deviceId = await PlatformUtil.getDeviceId()
      if (deviceId) {
        headers.set('x-device-id', deviceId)
      }
    }

    if (withPlatform) {
      headers.set('x-platform', platformInfo.os)
    }

    // Route all desktop API calls through the local proxy so that Tauri
    // webview and browser-local mode share one cookie jar (single sign-on).
    // - Tauri webview: http://localhost:<port>/proxy/... (trusted by origin)
    // - Browser-local: http://deluge.localhost:<port>/proxy/... (cookie auth)
    const fetchInit: RequestInit = { ...init, headers }
    // Mobile has no local browser_server proxy; forward through the Rust
    // `mobile_api_fetch` command (persisted cookie jar → session survives
    // restarts, cookie stays out of JS).
    const useMobileHttp = isTauriMobile()
    const fetchParams = useMobileHttp
      ? params
      : applyProxy(params, fetchInit, headers, proxySubPath)

    const fetch_ = useMobileHttp ? mobileApiFetch : globalThis.fetch

    let requestBody: unknown
    let requestBodyJson: string | undefined
    let requestStarted: number | undefined
    let pathname = ''
    // Logged in the browser too, when asked for: browser-local is how the
    // terminal is developed against a blade on this machine, and a request
    // nobody can see is a request nobody can debug.
    const shouldLog =
      LOG_API_ENABLED && (PlatformUtil.is('tauri') || isBrowserLocalMode())
    if (shouldLog) {
      requestStarted = performance.now()
      if (
        headers.get('Content-Type') === 'application/json' &&
        typeof init.body === 'string'
      ) {
        requestBodyJson = init.body
        requestBody = JSON.parse(init.body || 'null')
      }
      pathname = extractPathname(params)

      console.log(
        '%s %c %s %c %s %o',
        '[API REQUEST]',
        methodStyles[init.method?.toUpperCase() ?? 'DEFAULT'],
        init.method,
        '',
        pathname + '\n',
        {
          params: fetchParams,
          init: fetchInit,
          requestBody,
          requestBodyJson,
        },
      )
    }

    // Timed whether or not anybody asked for the log: this is the number the
    // person waiting actually experiences, network and queue included, and
    // the server's own histogram cannot see either.
    const callStartedAt = performance.now()
    const response = await fetch_(fetchParams, fetchInit)
    recordApiCall({
      path: pathOf(params),
      status: response.status,
      ms: performance.now() - callStartedAt,
    })

    // A session that has expired is renewed once, then the call is repeated.
    //
    // By the API itself, at its own refresh endpoint: the desktop app renews
    // through its local process because the cookie lives there, but a browser
    // holds the cookie and the API is what issued it. Asking the local
    // process from a browser is a connection refused, and the 401 that
    // started this then surfaces as "something went wrong" instead of "you
    // are signed out".
    if (
      response.status === 401 &&
      isBrowserLocalMode() &&
      !(init as Record<string, unknown>).__reissued
    ) {
      const requested = new URL(resolveRequestUrl(fetchParams))
      const refreshed = await fetch_(`${requested.origin}/api/user/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      }).catch(() => undefined)

      if (refreshed?.ok) {
        return createCustomFetch(args)(params, {
          ...init,
          __reissued: true,
        } as RequestInit)
      }
    }

    if (shouldLog) {
      let responseBody: unknown
      const responseHeaders = [...response.headers.entries()]
      try {
        responseBody = await response.clone().json()
        // eslint-disable-next-line no-empty
      } catch {}

      const requestDuration = requestStarted
        ? performance.now() - requestStarted
        : 0
      console.log(
        '%s %c %s %c %s %o %c %s %o',
        `[API RESPONSE]`,
        methodStyles[init.method?.toUpperCase() ?? 'DEFAULT'],
        [init.method, response.status, response.statusText]
          .filter(Boolean)
          .join(' '),
        '',
        pathname + '\n',
        { params, init, requestBody, requestBodyJson },
        '',
        '\n',
        {
          response,
          responseBody,
          responseHeaders,
          duration: `${(requestDuration / 1000).toFixed(2)}s`,
        },
      )
    }

    return response
  }
